import api, { APIError } from "api";
import { supportApiBase } from "api/constants";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { RolloutStatus } from "routes/healthForms/api";
import { queryKeys } from "utils/misc";

export const ENV_GROUPS = ["stage", "prod"] as const;

export type EnvGroup = (typeof ENV_GROUPS)[number];

export const isEnvGroup = (value: string): value is EnvGroup => ENV_GROUPS.some(group => group === value);

export type CatalogueKind = "exercise" | "lesson" | "template";

export type CatalogueGroupRollout = {
  status: RolloutStatus;
  has_undeployed_changes: boolean;
  deployed_at: string | null;
  deployed_by_email: string | null;
  confirmed_at: string | null;
};

export type CatalogueRollout = Partial<Record<EnvGroup, CatalogueGroupRollout>>;

const ROLLOUT_POLL_INTERVAL = 30_000;

export type RolloutCarrier = { rollout?: CatalogueRollout | null };

const rolloutPending = (rollout?: CatalogueRollout | null) =>
  Object.values(rollout ?? {}).some(group => group.status === "activating" || group.status === "retiring");

export const rolloutRefetchInterval = (items?: RolloutCarrier[]) =>
  items?.some(item => rolloutPending(item.rollout)) ? ROLLOUT_POLL_INTERVAL : false;

type CatalogueTarget = { kind: CatalogueKind; name: string; group: EnvGroup };

const invalidateDeployed = (queryClient: ReturnType<typeof useQueryClient>, { kind, name, group }: CatalogueTarget) => {
  queryClient.invalidateQueries(deployableCatalogueEntryKey(kind, name));
  queryClient.invalidateQueries(deployedCatalogueEntryKey(kind, name, group));
  queryClient.invalidateQueries(queryKeys.getExercises());
  queryClient.invalidateQueries(queryKeys.lessons);
  queryClient.invalidateQueries(queryKeys.templates());
};

export const useDeployCatalogueEntry = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ kind, name, group }: CatalogueTarget): Promise<void> => {
      await api.call(`/catalogue/${kind}/${encodeURIComponent(name)}/deployments`, {
        root: supportApiBase,
        method: "POST",
        data: { group },
      });
    },
    { onSuccess: (_result, target) => invalidateDeployed(queryClient, target) }
  );
};

export type DeployedExercise = {
  name: string;
  duration_minutes: number | null;
  target_joints: string[];
  primary_function: string;
  premium: boolean;
  retired: boolean;
  levels: {
    level: number;
    sets: number;
    reps: string;
    equipment: string[];
    duration_unit: string | null;
    duration_icon: string | null;
    movement: string | null;
    position: string | null;
    deprecated: boolean;
    translations: {
      locale: string;
      title: string;
      about: string | null;
      variation_title: string | null;
      tip_title: string | null;
      tip_description: string | null;
      instructions_icon_name: string;
      instructions_text1: string | null;
      instructions_text2: string;
      instructions_text3: string | null;
      image_url: string | null;
      video_url: string | null;
      video_file_url: string | null;
      video_thumbnail_file_url: string | null;
      caption_file_url: string | null;
      extra_swedish_description: string | null;
    }[];
  }[];
};

export type DeployedLesson = {
  name: string;
  target_joints: string[];
  retired: boolean;
  parts: {
    order: number;
    deprecated: boolean;
    questions: { order: number; correct_alternative: number }[];
    translations: {
      locale: string;
      title: string;
      content: { type: string; content: string }[];
      questions: { title: string; tip_description: string; answers: { text: string; correct: boolean }[] }[];
      image_url: string | null;
    }[];
  }[];
};

export type DeployedTemplate = {
  key: string;
  name: Record<string, string> | null;
  exercises_per_day: number;
  includes_functional_test: boolean;
  health_form_type: string;
  duration_weeks: number | null;
  group_key: string | null;
  markets?: string[] | null;
  retired: boolean;
  exercises: {
    exercise_name: string;
    days: number[] | null;
    weeks: number[] | null;
    recommended_level: number;
    recommended_sets: number | null;
    recommended_reps: string | null;
    level_locked: boolean;
    pinned: boolean;
  }[];
  lessons: { lesson_name: string; position: number }[];
  sustain_exercises: { exercise_name: string }[];
};

export type CatalogueEntry = {
  kind: CatalogueKind;
  name: string;
  exercise: DeployedExercise | null;
  lesson: DeployedLesson | null;
  template: DeployedTemplate | null;
};

export type DeployableCatalogueEntry = CatalogueEntry & RolloutCarrier;

export const deployedCatalogueEntryKey = (kind: CatalogueKind, name: string, group: EnvGroup) =>
  ["catalogue-deployed", kind, name, group] as const;

export const deployableCatalogueEntryKey = (kind: CatalogueKind, name: string) =>
  ["catalogue-deployable", kind, name] as const;

const errorStatus = (error: unknown): number | undefined => {
  try {
    return (JSON.parse((error as Error).message) as APIError).status;
  } catch (err) {
    return undefined;
  }
};

export const useGetDeployedCatalogueEntry = (kind: CatalogueKind, name: string, group: EnvGroup, enabled: boolean) =>
  useQuery<CatalogueEntry | null>(
    deployedCatalogueEntryKey(kind, name, group),
    async () => {
      try {
        // @ts-ignore — v2 endpoint, not in OpenAPI types
        return await api.parse(`GET /catalogue/${kind}/${encodeURIComponent(name)}/deployments/${group}`, {
          root: supportApiBase,
        });
      } catch (error) {
        if (errorStatus(error) === 404) return null;
        throw error;
      }
    },
    { enabled, retry: false, refetchOnMount: "always" }
  );

export const useGetDeployableCatalogueEntry = (kind: CatalogueKind, name: string, enabled: boolean) =>
  useQuery<DeployableCatalogueEntry>(
    deployableCatalogueEntryKey(kind, name),
    async () =>
      // @ts-ignore — v2 endpoint, not in OpenAPI types
      api.parse(`GET /catalogue/${kind}/${encodeURIComponent(name)}/deployable`, { root: supportApiBase }),
    {
      enabled,
      retry: false,
      refetchOnMount: "always",
      refetchInterval: (data?: DeployableCatalogueEntry) => rolloutRefetchInterval(data ? [data] : undefined),
    }
  );

export const useRevertCatalogueEntryToDeployed = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ kind, name, group }: CatalogueTarget): Promise<void> => {
      await api.call(`/catalogue/${kind}/${encodeURIComponent(name)}/deployments/${group}/revert`, {
        root: supportApiBase,
        method: "POST",
      });
    },
    {
      onSuccess: (_result, target) => {
        invalidateDeployed(queryClient, target);
        queryClient.invalidateQueries(deployableCatalogueEntryKey(target.kind, target.name));
        queryClient.invalidateQueries(queryKeys.templates());
      },
    }
  );
};
